import React, { useState, useEffect } from "react";

import { API, graphqlOperation } from "aws-amplify";

import {
  addDays,
  format,
  getDaysInMonth,
  startOfDay,
  startOfMonth,
  sub,
} from "date-fns";

import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Box, Typography } from "@material-ui/core";

import { useNotify } from "ra-core";

import { logError } from "../../../utils/appUtils";
import { searchInvoicesForReport } from "../../../graphql/report-queries";
import { BackButton } from "../../../components/common/BackButton";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { InvoiceStatus } from "../../../utils/constants";

// const CustomizedLabel = (props) => {
//   const { x, y, fill, value } = props;

//   return (
//     <text
//       x={x}
//       y={y}
//       dy={-4}
//       fontSize="16"
//       fontFamily="sans-serif"
//       fill={fill}
//       textAnchor="middle"
//     >
//       {value}
//     </text>
//   );
// };

export const DailyRevenueChart = ({ location }) => {
  const notify = useNotify();

  // Determine how many days to show based on the month/year in the query,
  // falling back to the current month when there is no explicit month.
  const params = new URLSearchParams(location?.search || "");
  const monthStartParam = params.get("monthStart");
  const baseDate = monthStartParam ? new Date(monthStartParam) : new Date();
  const daysInView = getDaysInMonth(baseDate);
  const emptyData = Array.from({ length: daysInView }, () => ({}));

  const [data, setData] = useState(emptyData);
  const [average, setAverage] = useState(0);

  useEffect(() => {
    getDailyRevenueData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const getDailyRevenueData = async () => {
    let startDate;
    let endDate;

    try {
      if (monthStartParam) {
        const monthStartDate = startOfMonth(new Date(monthStartParam));
        startDate = monthStartDate;
        endDate = addDays(startDate, 31); // safe upper bound; we'll rely on dates from invoices
      } else {
        // Default: last 14 days
        endDate = startOfDay(new Date());
        startDate = startOfDay(sub(endDate, { days: 14 }));
      }

      const invoices = await getInvoicesForRange(startDate, endDate);

      // Aggregate totals by calendar day (yyyy-MM-dd)
      const aggregatedData = {};
      invoices.forEach((invoice) => {
        if (!invoice.createdAt) {
          return;
        }
        const dateKey = format(new Date(invoice.createdAt), "yyyy-MM-dd");
        const value = invoice.total || 0;
        if (!aggregatedData[dateKey]) {
          aggregatedData[dateKey] = value;
        } else {
          aggregatedData[dateKey] += value;
        }
      });

      // Build a complete series with one entry per day in the month in view,
      // then overlay the aggregated amounts so that every day index exists
      const monthStartDate = startOfMonth(baseDate);
      const nextData = Array.from({ length: daysInView }, (_, idx) => {
        const dayDate = addDays(monthStartDate, idx);
        return {
          name: format(dayDate, "d"),
          date: dayDate.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          }),
        };
      });

      let total = 0;
      let length = 0;

      Object.keys(aggregatedData).forEach((key) => {
        const dateObj = new Date(key);
        const dayIndex = dateObj.getDate() - 1; // 0-based index for day of month
        if (dayIndex < 0 || dayIndex >= daysInView) {
          return;
        }

        const amt = aggregatedData[key];
        nextData[dayIndex] = {
          ...nextData[dayIndex],
          amt,
        };

        if (amt != null) {
          total += amt;
          length += 1;
        }
      });

      const avg = length > 0 ? Math.round(total / length) : 0;

      setData(nextData);
      setAverage(avg);
    } catch (err) {
      logError("DailyRevenueChart:getDailyRevenueData", err, notify);
      setData(emptyData);
      setAverage(0);
    }
  };

  const getInvoicesForRange = async (start, end) => {
    const yardId = window.appUser?.reloadYardId;
    let nextToken = undefined;
    const invoices = [];

    const baseInput = {
      filter: {
        active: { eq: "true" },
        reloadYardId: { eq: yardId },
        status: { eq: InvoiceStatus.PAID },
        and: [
          { createdAt: { gte: start.toISOString() } },
          { createdAt: { lt: end.toISOString() } },
        ],
      },
      sort: { field: "createdAt", direction: "asc" },
      limit: 1000,
    };

    try {
      do {
        const input = nextToken ? { ...baseInput, nextToken } : baseInput;

        // eslint-disable-next-line no-await-in-loop
        const result = await API.graphql(
          graphqlOperation(searchInvoicesForReport, input)
        );

        const { items, nextToken: newNextToken } = result.data.searchInvoices;

        if (items && items.length > 0) {
          invoices.push(...items);
        }

        nextToken = newNextToken;
      } while (nextToken);
    } catch (err) {
      logError("DailyRevenueChart:getInvoicesForRange", err, notify);
    }

    return invoices;
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <ForceTitle title="Daily Revenue" />
        <BackButton basePath="/reports" />
      </Box>
      <ResponsiveContainer>
        <BarChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" tickSize dy="25" />
          <YAxis />
          <Tooltip
            content={({ payload }) => {
              if (payload && payload.length) {
                const { date, amt } = payload[0].payload;
                return (
                  <div
                    style={{
                      color: "black",
                      background: "white",
                      padding: "8px",
                      border: "1px solid black",
                    }}
                  >
                    <Typography>{`Date: ${date}`}</Typography>
                    <Typography>{`Revenue: ${amt}`}</Typography>
                    <Typography>{`Average: ${average}`}</Typography>
                  </div>
                );
              }
              return null;
            }}
          />
          {average && (
            <ReferenceLine
              y={average}
              stroke="red"
              strokeWidth={6}
              strokeDasharray="6 6"
            />
          )}
          <Bar
            dataKey="amt"
            stroke="blue"
            // label={<CustomizedLabel />}
          />
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};
